import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { BellRing, CheckCircle2, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "../lib/api";
import { PROVINCES } from "../lib/constants";
import { Switch } from "../components/ui/switch";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const T = {
  en: { title: "Your email preferences", sub: "Choose what CritterPosts sends to", news: "Newsletter", news_d: "Pet news, tips and the newest pets, now and then.",
    alerts: "Lost-pet alerts", alerts_d: "An email when a pet is lost or found near you.", city: "City", prov: "Province", dist: "Distance", whole: "Whole province",
    save: "Save preferences", saved: "Preferences saved", off: "You're unsubscribed from everything. You can turn emails back on here any time.",
    bad: "This link is invalid or expired.", home: "Back to CritterPosts", lang: "Email language", choose: "Choose…" },
  fr: { title: "Vos préférences de courriel", sub: "Choisissez ce que CritterPosts envoie à", news: "Infolettre", news_d: "Nouvelles, conseils et nouveaux animaux, de temps en temps.",
    alerts: "Alertes d'animaux perdus", alerts_d: "Un courriel quand un animal est perdu ou trouvé près de chez vous.", city: "Ville", prov: "Province", dist: "Distance", whole: "Toute la province",
    save: "Enregistrer", saved: "Préférences enregistrées", off: "Vous êtes désabonné(e) de tout. Vous pouvez réactiver les courriels ici en tout temps.",
    bad: "Ce lien est invalide ou expiré.", home: "Retour à CritterPosts", lang: "Langue des courriels", choose: "Choisir…" },
};

const PROV_FR = { BC: "Colombie-Britannique", NB: "Nouveau-Brunswick", NL: "Terre-Neuve-et-Labrador", NS: "Nouvelle-Écosse", NT: "Territoires du Nord-Ouest", PE: "Île-du-Prince-Édouard", QC: "Québec" };

const Row = ({ icon: Icon, title, desc, on, onChange, testid, children }) => (
  <div className="rounded-2xl border border-slate-200 p-5" data-testid={testid}>
    <div className="flex items-start gap-4">
      <span className="h-10 w-10 rounded-xl bg-[#12234A] flex items-center justify-center shrink-0"><Icon size={18} className="text-[#F5551E]" /></span>
      <div className="flex-1"><p className="font-bold text-[#12234A]">{title}</p><p className="text-sm text-slate-500">{desc}</p></div>
      <Switch checked={on} onCheckedChange={onChange} data-testid={`${testid}-switch`} />
    </div>
    {on && children}
  </div>
);

export default function SubscriptionPrefs() {
  const [params] = useSearchParams();
  const s = params.get("s") || "", t = params.get("t") || "";
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const [busy, setBusy] = useState(false);
  const [off, setOff] = useState(false);
  useEffect(() => { api.get("/subscriptions/prefs", { params: { s, t } }).then(({ data }) => setD(data)).catch(() => setErr(true)); }, [s, t]);
  const L = T[d?.lang === "fr" ? "fr" : "en"];
  if (err) return <div className="max-w-lg mx-auto px-4 py-24 text-center" data-testid="prefs-invalid"><p className="text-slate-500">{T.en.bad} / {T.fr.bad}</p><Link to="/" className="text-[#F5551E] font-semibold mt-4 inline-block">{T.en.home}</Link></div>;
  if (!d) return <div className="py-32 flex justify-center"><Loader2 className="animate-spin text-slate-300" /></div>;
  const a = d.alerts;
  const setA = (k, v) => setD({ ...d, alerts: { ...a, [k]: v } });
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/subscriptions/prefs", { s, t, newsletter: d.newsletter, alerts_enabled: a.enabled, city: a.city, province: a.province, radius_km: a.radius_km, lang: d.lang });
      setOff(data.unsubscribed);
      toast.success(T[d.lang === "fr" ? "fr" : "en"].saved);
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const sel = "h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm";
  return (
    <div className="max-w-xl mx-auto px-4 py-14" data-testid="subscription-prefs">
      <h1 className="font-display text-3xl font-extrabold text-[#12234A]">{L.title}</h1>
      <p className="text-slate-500 mt-2">{L.sub} <strong className="text-[#12234A]" data-testid="prefs-email">{d.email}</strong></p>
      <div className="mt-8 space-y-4">
        <Row icon={Mail} title={L.news} desc={L.news_d} on={d.newsletter} onChange={(v) => setD({ ...d, newsletter: v })} testid="prefs-newsletter" />
        <Row icon={BellRing} title={L.alerts} desc={L.alerts_d} on={a.enabled} onChange={(v) => setA("enabled", v)} testid="prefs-alerts">
          <div className="grid sm:grid-cols-3 gap-3 mt-4">
            <label className="text-xs font-semibold text-slate-500">{L.city}<Input value={a.city} onChange={(e) => setA("city", e.target.value)} className="mt-1 rounded-xl" data-testid="prefs-city" /></label>
            <label className="text-xs font-semibold text-slate-500">{L.prov}
              <select value={a.province} onChange={(e) => setA("province", e.target.value)} className={`${sel} mt-1`} data-testid="prefs-province">
                <option value="">{L.choose}</option>
                {PROVINCES.filter((p) => d.provinces.includes(p.code)).map((p) => <option key={p.code} value={p.code}>{(d.lang === "fr" && PROV_FR[p.code]) || p.name}</option>)}
              </select>
            </label>
            <label className="text-xs font-semibold text-slate-500">{L.dist}
              <select value={a.radius_km} onChange={(e) => setA("radius_km", Number(e.target.value))} className={`${sel} mt-1`} data-testid="prefs-radius">
                {d.radius_options.map((r) => <option key={r} value={r}>{r === 0 ? L.whole : `${r} km`}</option>)}
              </select>
            </label>
          </div>
        </Row>
        <label className="flex items-center justify-between rounded-2xl border border-slate-200 p-5">
          <span className="font-bold text-[#12234A] text-sm">{L.lang}</span>
          <select value={d.lang} onChange={(e) => setD({ ...d, lang: e.target.value })} className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm" data-testid="prefs-lang">
            <option value="en">English</option><option value="fr">Français</option>
          </select>
        </label>
      </div>
      {off && <p className="mt-5 text-sm text-slate-500 flex items-start gap-2" data-testid="prefs-off-note"><CheckCircle2 size={16} className="text-emerald-500 mt-0.5 shrink-0" /> {L.off}</p>}
      <Button onClick={save} disabled={busy} className="mt-6 w-full h-12 rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white font-semibold" data-testid="prefs-save">
        {busy ? <Loader2 size={16} className="animate-spin" /> : L.save}
      </Button>
    </div>
  );
}
